/**
 * Baseline "Cost of Doing Nothing" calculation
 *
 * Estimates annual and lifetime costs of keeping the current system,
 * using seasonal efficiency data and optional sludge/water quality degradation.
 *
 * IMPORTANT: This module consumes the canonical SeasonalEfficiency type.
 * In UK domestic context, SEDBUK IS seasonal efficiency - they are the same concept.
 */

import type { ContaminationSeverity } from './hydronics/contamination';
import { calculateBaselineLongevity, type LongevityMetrics } from './longevity';
import {
  deriveSedbukBand,
  formatSeasonalPct,
  formatSedbukDisplay,
} from './efficiency';
import type { SeasonalEfficiency } from './types';
import {
  isPastDesignLife,
  getDesignLifeWarning,
} from './brandDesignLife';

// ==================== TYPES ====================

export type SludgeRisk = 'NONE' | 'POSSIBLE' | 'LIKELY' | 'CONFIRMED';
export type EfficiencySource = 'SEDBUK' | 'EPC_BAND' | 'USER' | 'ASSUMED';
export type EPCBand = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
export type SedbukBand = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
export type BaselineFuelType = 'gas' | 'oil' | 'electric' | 'lpg';

export interface BaselineInput {
  // Heat demand
  annualHeatDemand_kWh: number;           // Total useful heat needed per year

  // ========== CANONICAL EFFICIENCY (preferred) ==========
  // Use this single field for all efficiency data. It contains value, source, and confidence.
  seasonalEfficiency?: SeasonalEfficiency;

  // ========== LEGACY EFFICIENCY FIELDS (deprecated) ==========
  // These fields are kept for backward compatibility but should not be used for new code.
  // If seasonalEfficiency is provided, these are ignored.
  efficiencySource?: EfficiencySource;     // @deprecated - use seasonalEfficiency.source
  efficiencyValue?: number;                // @deprecated - use seasonalEfficiency.value
  epcBand?: EPCBand;                       // @deprecated - use seasonalEfficiency
  sedbukRating?: number;                   // @deprecated - use seasonalEfficiency
  sedbukBand?: SedbukBand;                 // @deprecated - use seasonalEfficiency.band
  sedbukSeasonalEfficiency?: number;       // @deprecated - use seasonalEfficiency.value
  sedbukSource?: string;                   // @deprecated - use seasonalEfficiency.source
  sedbukConfidence?: 'high' | 'medium' | 'low';  // @deprecated - use seasonalEfficiency.confidence

  // Fuel costs
  fuelType: BaselineFuelType;
  fuelUnitPrice_pPerKwh: number;           // Pence per kWh
  standingCharge_gbpPerYear?: number;      // Optional

  // Water quality / sludge risk
  sludgeRisk: SludgeRisk;

  // System characteristics
  systemAge_years?: number;
  
  // Boiler age for design-life assessment
  boilerMake?: string;                     // Boiler manufacturer/brand
  boilerAge_years?: number;                // Exact age of boiler (normalized from install year or direct age)

  // LCOO analysis period
  analysisYears: number;
}

export interface YearlyBaseline {
  year: number;
  efficiency: number;                      // Degraded efficiency for this year
  inputEnergy_kWh: number;                 // Energy consumed
  fuelCost_gbp: number;                    // Fuel cost only
  unplannedMaintenance_gbp: number;        // Risk allowance
  totalAnnualCost_gbp: number;             // Fuel + maintenance
}

export interface BaselineResult {
  source: string;                          // Description of efficiency source
  initialEfficiency: number;               // Starting efficiency (0-1) - raw value before sludge penalty
  sludgeAdjustedEfficiency?: number;       // Efficiency after applying sludge penalty (for carbon calculations)
  finalEfficiency: number;                 // Efficiency at end of period

  yearlyBreakdown: YearlyBaseline[];       // Per-year costs

  totalFuelCost_gbp: number;               // Sum of fuel costs
  totalMaintenanceCost_gbp: number;        // Sum of maintenance allowances
  totalCost_gbp: number;                   // Total over analysis period
  annualizedCost_gbp: number;              // Average per year
  periodYears: number;                     // Analysis period length

  longevity?: {                            // Longevity estimates for existing system
    expectedLifespanYears: { min: number; max: number };
    remainingLifespan_years?: number;      // Estimated remaining life
    expectedReplacementsInPeriod: number;  // Replacements needed in analysis period
    reasoning: string;
    pastDesignLife?: boolean;              // True if boiler exceeds typical design life
    designLifeWarning?: string;            // User-facing warning about design life
  };

  hydronicsInfo?: {                        // Sludge/contamination impact
    severity: string;
    efficiencyPenalty?: number;
  };

  efficiencySource?: string;               // Source description for UI

  assumptions: string[];                   // Human-readable assumptions
  warnings: string[];                      // Important caveats
}

// ==================== CONFIGURATION ====================

export const BASELINE_CONFIG = {
  // EPC band to efficiency mapping (seasonal efficiency estimates)
  epcEfficiency: {
    A: 0.92,
    B: 0.88,
    C: 0.84,
    D: 0.78,
    E: 0.70,
    F: 0.62,
    G: 0.54,
  } as Record<EPCBand, number>,

  // SEDBUK rating to efficiency (SEDBUK is already seasonal efficiency %)
  sedbukToEfficiency: (rating: number) => rating / 100,

  // Sludge immediate efficiency penalty
  sludgeImmediatePenalty: {
    NONE: 0.00,
    POSSIBLE: 0.02,      // 2% immediate hit
    LIKELY: 0.05,        // 5% immediate hit
    CONFIRMED: 0.08,     // 8% immediate hit
  } as Record<SludgeRisk, number>,

  // Sludge degradation rate per year (compounding)
  sludgeDegradationRate: {
    NONE: 0.0000,
    POSSIBLE: 0.0025,    // 0.25% per year
    LIKELY: 0.0050,      // 0.50% per year
    CONFIRMED: 0.0100,   // 1.00% per year
  } as Record<SludgeRisk, number>,

  // Maximum efficiency degradation cap
  maxDegradation: 0.12,  // Can't lose more than 12% total efficiency
  
  // Physical minimum efficiency floor (absolute, not relative)
  // Even the worst-maintained boiler still functions at some minimum level
  physicalMinEfficiency: 0.40,  // 40% absolute minimum (prevents unrealistic calculations)

  // Unplanned maintenance allowance per year (GBP)
  unplannedMaintenanceAllowance: {
    NONE: 0,
    POSSIBLE: 20,
    LIKELY: 60,
    CONFIRMED: 120,
  } as Record<SludgeRisk, number>,

  // Default assumptions
  defaults: {
    assumedEfficiency: 0.78,               // Default if nothing else available (EPC D equivalent)
    analysisYears: 20,
    gasPriceIfUnknown: 5.93,               // p/kWh
  },
};

// ==================== CONTAMINATION SEVERITY -> SLUDGE RISK MAPPING ====================

/**
 * Map contamination severity (from hydronic assessment) to sludge risk tier
 */
export function contaminationSeverityToSludgeRisk(severity: ContaminationSeverity): SludgeRisk {
  const mapping: Record<ContaminationSeverity, SludgeRisk> = {
    low: 'NONE',
    moderate: 'POSSIBLE',
    high: 'LIKELY',
    severe: 'CONFIRMED',
  };
  return mapping[severity];
}

// ==================== EFFICIENCY RESOLUTION ====================

/**
 * Resolve initial efficiency from input sources
 *
 * Priority order:
 * 1. CANONICAL - seasonalEfficiency object (preferred, single source of truth)
 * 2. USER - User-provided efficiency value (legacy)
 * 3. SEDBUK - From GC number or make/model lookup (legacy)
 * 4. EPC_BAND - From EPC certificate (legacy)
 * 5. ASSUMED - Default fallback (78% = EPC D equivalent)
 *
 * Uses canonical formatting helpers for consistent display.
 */
function resolveInitialEfficiency(input: BaselineInput): { efficiency: number; source: string } {
  const config = BASELINE_CONFIG;

  // ========== CANONICAL PATH (preferred) ==========
  // If seasonalEfficiency is provided, use it directly - no need to check other fields
  if (input.seasonalEfficiency) {
    return {
      efficiency: input.seasonalEfficiency.value,
      source: input.seasonalEfficiency.sourceDescription ||
              `Seasonal efficiency ${formatSeasonalPct(input.seasonalEfficiency.value)}% (${input.seasonalEfficiency.source})`,
    };
  }

  // ========== LEGACY PATH (backward compatibility) ==========

  if (input.efficiencySource === 'USER' && input.efficiencyValue) {
    const pct = formatSeasonalPct(input.efficiencyValue);
    return {
      efficiency: input.efficiencyValue,
      source: `User-provided efficiency: ${pct}%`,
    };
  }

  if (input.efficiencySource === 'SEDBUK') {
    // Prefer new SEDBUK fields (from resolveSedbuk)
    if (typeof input.sedbukSeasonalEfficiency === 'number') {
      // Use provided band or derive using canonical helper
      const band = input.sedbukBand || deriveSedbukBand(input.sedbukSeasonalEfficiency);
      // Use canonical display format
      const displayLabel = formatSedbukDisplay(input.sedbukSeasonalEfficiency, band as SedbukBand);
      const confidenceNote = input.sedbukConfidence === 'high' ? '' : ` (${input.sedbukConfidence} confidence)`;
      return {
        efficiency: input.sedbukSeasonalEfficiency,
        source: `${displayLabel}${confidenceNote}`,
      };
    }

    // Fallback to legacy sedbukRating (0-100 scale) for backward compatibility
    if (typeof input.sedbukRating === 'number') {
      const eff = config.sedbukToEfficiency(input.sedbukRating);
      // Use canonical display format
      const displayLabel = formatSedbukDisplay(eff);
      return {
        efficiency: eff,
        source: displayLabel,
      };
    }
  }

  if (input.efficiencySource === 'EPC_BAND' && input.epcBand) {
    const eff = config.epcEfficiency[input.epcBand];
    if (eff) {
      const pct = formatSeasonalPct(eff);
      return {
        efficiency: eff,
        source: `EPC band ${input.epcBand} (estimated efficiency ${pct}%)`,
      };
    }
  }

  // Fallback
  const defaultPct = formatSeasonalPct(config.defaults.assumedEfficiency);
  return {
    efficiency: config.defaults.assumedEfficiency,
    source: `Assumed efficiency ${defaultPct}% (no efficiency data provided)`,
  };
}

/**
 * Derive SEDBUK band from efficiency value (0-1 scale)
 * Used when only efficiency % is known, not the actual band
 *
 * @deprecated Use deriveSedbukBand from './efficiency' instead for canonical band derivation
 */
export function deriveBandFromEfficiency(efficiency: number): 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' {
  // Delegate to canonical helper for consistency
  return deriveSedbukBand(efficiency);
}

// ==================== BASELINE CALCULATION ====================

/**
 * Check if efficiency band is considered "low" (F or G)
 * Low bands indicate systems already in poor condition, potentially due to sludge
 */
function isLowEfficiencyBand(band: string | undefined): boolean {
  return band === 'F' || band === 'G';
}

/**
 * Calculate "Do Nothing" baseline costs over analysis period
 */
export function calculateBaselineCost(input: BaselineInput): BaselineResult {
  const config = BASELINE_CONFIG;
  const assumptions: string[] = [];
  const warnings: string[] = [];

  // 1. Resolve initial efficiency
  const { efficiency: initialEff, source: effSource } = resolveInitialEfficiency(input);
  assumptions.push(effSource);

  // 2. Apply immediate sludge penalty (with double-counting protection)
  let immediatePenalty = config.sludgeImmediatePenalty[input.sludgeRisk];
  
  // CRITICAL FIX: Prevent double-counting when user manually selected a low efficiency band
  // If user selected Band F or G, they've likely already accounted for sludge in their selection
  // Reduce the immediate penalty to avoid over-penalizing
  const userBand = input.sedbukBand || input.seasonalEfficiency?.band;
  const isUserSelectedLowBand = (
    (input.efficiencySource === 'USER' || input.seasonalEfficiency?.source === 'SEDBUK_BAND') &&
    isLowEfficiencyBand(userBand)
  );
  
  if (isUserSelectedLowBand && immediatePenalty > 0) {
    // Reduce immediate penalty by 50% to avoid double-counting
    // Still apply degradation rate to model future decline
    const originalPenalty = immediatePenalty;
    immediatePenalty = immediatePenalty * 0.5;
    warnings.push(
      `Note: Sludge penalty reduced (${(originalPenalty * 100).toFixed(1)}% → ${(immediatePenalty * 100).toFixed(1)}%) because low efficiency band (${userBand}) likely already reflects poor system condition`
    );
  }
  
  const eff0 = initialEff * (1 - immediatePenalty);

  if (input.sludgeRisk !== 'NONE') {
    assumptions.push(
      'Sludge risk tier: ' + input.sludgeRisk + ' (immediate ' + (immediatePenalty * 100).toFixed(1) + '% efficiency penalty)'
    );
  }

  // 3. Get degradation parameters
  const degradationRate = config.sludgeDegradationRate[input.sludgeRisk];
  const unplannedAllowance = config.unplannedMaintenanceAllowance[input.sludgeRisk];

  // VALIDATION: Cross-reference sludge risk with system age for consistency
  if (input.systemAge_years !== undefined) {
    // Young systems (< 5 years) with CONFIRMED sludge are unusual
    if (input.systemAge_years < 5 && input.sludgeRisk === 'CONFIRMED') {
      warnings.push(
        `Warning: System age (${input.systemAge_years} years) is young for CONFIRMED sludge risk. Consider if contamination is from another source or if sludge risk should be LIKELY instead.`
      );
    }
    
    // Very old systems (> 15 years) with NONE sludge risk are optimistic
    if (input.systemAge_years > 15 && input.sludgeRisk === 'NONE') {
      warnings.push(
        `Note: System age (${input.systemAge_years} years) suggests potential for sludge build-up. Consider assessing for POSSIBLE sludge risk.`
      );
    }
  }
  
  // VALIDATION: Ensure total efficiency loss doesn't exceed maxDegradation cap
  const totalImmediateLoss = immediatePenalty;
  const projectedDegradation = degradationRate * input.analysisYears;
  const totalPotentialLoss = totalImmediateLoss + projectedDegradation;
  
  if (totalPotentialLoss > config.maxDegradation) {
    assumptions.push(
      `Note: Total efficiency loss capped at ${(config.maxDegradation * 100).toFixed(0)}% (immediate ${(totalImmediateLoss * 100).toFixed(1)}% + up to ${(projectedDegradation * 100).toFixed(1)}% degradation over ${input.analysisYears} years)`
    );
  }

  if (degradationRate > 0) {
    assumptions.push(
      'Efficiency degrades at ' + (degradationRate * 100).toFixed(2) + '% per year (compounding, capped at ' + (config.maxDegradation * 100).toFixed(0) + '%)'
    );
  }

  if (unplannedAllowance > 0) {
    assumptions.push(
      'Unplanned maintenance allowance: £' + unplannedAllowance + '/year (sludge-related risk)'
    );
  }

  // 4. Calculate year-by-year
  const yearlyBreakdown: YearlyBaseline[] = [];
  let totalFuelCost = 0;
  let totalMaintenance = 0;

  for (let year = 1; year <= input.analysisYears; year++) {
    // Apply degradation (compounding, capped)
    const degradationFactor = Math.pow(1 - degradationRate, year - 1);
    // Use both relative cap (maxDegradation) AND absolute physical floor
    const relativeMinEfficiency = eff0 * (1 - config.maxDegradation);
    const absoluteMinEfficiency = config.physicalMinEfficiency;
    const minEfficiency = Math.max(relativeMinEfficiency, absoluteMinEfficiency);
    const effThisYear = Math.max(eff0 * degradationFactor, minEfficiency);

    // Calculate costs
    const inputEnergy = input.annualHeatDemand_kWh / effThisYear;
    const fuelCost = (inputEnergy * input.fuelUnitPrice_pPerKwh) / 100; // Convert pence to pounds
    const maintenanceCost = unplannedAllowance;
    const totalCost = fuelCost + maintenanceCost;

    yearlyBreakdown.push({
      year,
      efficiency: effThisYear,
      inputEnergy_kWh: inputEnergy,
      fuelCost_gbp: fuelCost,
      unplannedMaintenance_gbp: maintenanceCost,
      totalAnnualCost_gbp: totalCost,
    });

    totalFuelCost += fuelCost;
    totalMaintenance += maintenanceCost;
  }

  const totalCost = totalFuelCost + totalMaintenance;
  const annualizedCost = totalCost / input.analysisYears;
  const finalEfficiency = yearlyBreakdown[yearlyBreakdown.length - 1].efficiency;
  
  // Check if physical floor was hit
  if (finalEfficiency <= config.physicalMinEfficiency + 0.01) { // Small tolerance for floating point
    warnings.push(
      `Warning: System efficiency has degraded to the physical minimum of ${(config.physicalMinEfficiency * 100).toFixed(0)}%. ` +
      `Such low efficiency indicates a system in critical condition requiring immediate replacement. ` +
      `Actual costs may be even higher due to increased breakdown risk.`
    );
  }

  // 5. Add standing charge if provided
  if (input.standingCharge_gbpPerYear) {
    const totalStandingCharge = input.standingCharge_gbpPerYear * input.analysisYears;
    assumptions.push('Standing charge: £' + input.standingCharge_gbpPerYear.toFixed(2) + '/year (excluded from comparison)');
    warnings.push('Standing charge (£' + totalStandingCharge.toFixed(0) + ' over ' + input.analysisYears + ' years) not included in total - applies to all options equally');
  }

  // 6. Add warnings
  if (input.sludgeRisk === 'CONFIRMED' || input.sludgeRisk === 'LIKELY') {
    warnings.push('High sludge risk increases breakdown probability and may shorten boiler lifespan - actual costs may exceed this baseline');
  }

  if (finalEfficiency < initialEff * 0.90) {
    const effLoss = ((initialEff - finalEfficiency) / initialEff) * 100;
    warnings.push('Efficiency degrades by ' + effLoss.toFixed(1) + '% over ' + input.analysisYears + ' years if system not remediated');
  }

  assumptions.push('Heat demand: ' + input.annualHeatDemand_kWh.toLocaleString() + ' kWh/year');
  assumptions.push('Fuel price: ' + input.fuelUnitPrice_pPerKwh.toFixed(2) + ' p/kWh (' + input.fuelType + ')');

  // 7. Calculate longevity for existing system
  const longevityMetrics = calculateBaselineLongevity(
    'existing',
    input.systemAge_years,
    input.analysisYears
  );

  // Calculate remaining lifespan for UI display
  const avgLifespan = (longevityMetrics.expectedLifespanYears.min + longevityMetrics.expectedLifespanYears.max) / 2;
  const effectiveAge = input.systemAge_years || avgLifespan * 0.7;
  const remainingLife = Math.max(Math.round(avgLifespan - effectiveAge), 1);
  
  // 8. Check for past design-life condition
  const pastDesignLife = isPastDesignLife(input.boilerMake, input.boilerAge_years);
  const designLifeWarning = getDesignLifeWarning(input.boilerAge_years, input.boilerMake);
  
  if (designLifeWarning) {
    warnings.push(designLifeWarning);
  }

  // 9. Prepare hydronics info for UI
  const hydronicsInfo = input.sludgeRisk !== 'NONE' ? {
    severity: input.sludgeRisk.toLowerCase(),
    efficiencyPenalty: immediatePenalty
  } : undefined;

  return {
    source: effSource,
    initialEfficiency: initialEff,  // Raw efficiency before sludge penalty (for display)
    sludgeAdjustedEfficiency: eff0,  // Sludge-adjusted efficiency (for carbon calculations)
    finalEfficiency,
    yearlyBreakdown,
    totalFuelCost_gbp: totalFuelCost,
    totalMaintenanceCost_gbp: totalMaintenance,
    totalCost_gbp: totalCost,
    annualizedCost_gbp: annualizedCost,
    periodYears: input.analysisYears,
    longevity: {
      expectedLifespanYears: longevityMetrics.expectedLifespanYears,
      remainingLifespan_years: remainingLife,
      expectedReplacementsInPeriod: longevityMetrics.replacementsInPeriod,
      reasoning: longevityMetrics.reasoning,
      pastDesignLife,
      designLifeWarning,
    },
    hydronicsInfo,
    efficiencySource: effSource,
    assumptions,
    warnings,
  };
}

// ==================== CONVENIENCE HELPERS ====================

/**
 * Estimate heat demand from heat loss (kW) if not provided
 */
export function estimateAnnualHeatDemand(heatLoss_kW: number): number {
  // Typical UK: ~2000 heating hours/year at ~70% average load factor
  const heatingHours = 2000;
  const loadFactor = 0.70;
  return heatLoss_kW * heatingHours * loadFactor;
}

/**
 * Quick comparison: new system vs baseline
 */
export interface BaselineComparison {
  baselineAnnualized_gbp: number;
  optionAnnualized_gbp: number;
  annualSaving_gbp: number;           // Positive = saving, negative = extra cost
  totalSaving_gbp: number;            // Over analysis period
  breakEvenYears: number | null;      // Years to recoup upfront cost difference
}

export function compareToBaseline(
  baseline: BaselineResult,
  optionTotalCost_gbp: number,
  optionUpfrontCost_gbp: number,
  baselineUpfrontCost_gbp: number,
  analysisYears: number
): BaselineComparison {
  const optionAnnualized = optionTotalCost_gbp / analysisYears;
  const annualSaving = baseline.annualizedCost_gbp - optionAnnualized;
  const totalSaving = baseline.totalCost_gbp - optionTotalCost_gbp;

  // Break-even calculation
  const upfrontDelta = optionUpfrontCost_gbp - baselineUpfrontCost_gbp;
  let breakEvenYears: number | null = null;

  if (annualSaving > 0 && upfrontDelta > 0) {
    breakEvenYears = upfrontDelta / annualSaving;
  }

  return {
    baselineAnnualized_gbp: baseline.annualizedCost_gbp,
    optionAnnualized_gbp: optionAnnualized,
    annualSaving_gbp: annualSaving,
    totalSaving_gbp: totalSaving,
    breakEvenYears,
  };
}
